import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import "./Debts.css";
import PortalModal from "../components/PortalModal";

export default function Debts() {
  const today = new Date().toISOString().split("T")[0];

  // New Creditor form
  const [newCreditor, setNewCreditor] = useState("");
  const [newCreditorDesc, setNewCreditorDesc] = useState("");

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [creditorFilter, setCreditorFilter] = useState("");

  // Main data
  const [creditors, setCreditors] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Modals & forms
  const [showLedger, setShowLedger] = useState(false);
  const [ledgerCreditor, setLedgerCreditor] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [showRepayForm, setShowRepayForm] = useState(false);
  const [formAmount, setFormAmount] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [isCorrection, setIsCorrection] = useState(false);

  const fetchData = async () => {
    const { data: debts } = await supabase.from("debts").select("*").order("creditor");
    const { data: trans } = await supabase.from("debt_transactions").select("*").order("date", { ascending: true });
    setCreditors(debts || []);
    setTransactions(trans || []);
  };

  useEffect(() => { fetchData(); }, []);

  // Add new creditor
  const addCreditor = async () => {
    if (!newCreditor.trim()) {
      toast.error("Creditor name is required");
      return;
    }
    if (newCreditorDesc.length > 255) {
      toast.error("Description too long (max 255 chars)");
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("debts").insert([{
      creditor: newCreditor.trim(),
      description: newCreditorDesc.trim(),
      status: "Active",
      logged_by: user?.user_metadata?.display_name || "Manual Entry",
      logged_at: new Date().toISOString()
    }]);
    setNewCreditor("");
    setNewCreditorDesc("");
    toast.success("Creditor added");
    fetchData();
  };

  // Calculate totals
  const calculateTotals = (debtId) => {
    const rows = transactions.filter(t => t.debt_id === debtId)
      .filter(t => (!fromDate || t.date >= fromDate) && (!toDate || t.date <= toDate));

    const totalLoaned = rows.filter(r => r.type === "Loan").reduce((sum, r) => sum + r.amount, 0);
    const totalPaid = rows.filter(r => r.type === "Payment").reduce((sum, r) => sum + r.amount, 0);
    const balance = totalLoaned - totalPaid;
    return { totalLoaned, totalPaid, balance };
  };

  // Ledger view
  const openLedger = (creditor) => {
    setLedgerCreditor(creditor);
    const ledgerRows = transactions.filter(t => t.debt_id === creditor.id)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    setLedgerData(ledgerRows);
    setShowLedger(true);
  };

  // Add transaction
  const addTransaction = async (type) => {
    const amount = parseFloat(formAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid positive amount");
      return;
    }
    if (!formNotes.trim()) {
      toast.error("Notes are required");
      return;
    }
    let notesToSave = formNotes.trim();
    if (isCorrection && !notesToSave.toLowerCase().startsWith("correction:")) {
      notesToSave = "Correction: " + notesToSave;
    }
    if (type === "Payment") {
      const currentBalance = calculateTotals(ledgerCreditor.id).balance;
      if (amount > currentBalance) {
        toast.error("Repayment cannot exceed current balance");
        return;
      }
    }

    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("debt_transactions").insert([{
      debt_id: ledgerCreditor.id,
      date: today,
      type,
      amount,
      notes: notesToSave,
      logged_by: user?.user_metadata?.display_name || "Manual Entry",
      logged_at: new Date().toISOString()
    }]);

    setFormAmount(""); 
    setFormNotes("");
    setIsCorrection(false);
    setShowLoanForm(false); 
    setShowRepayForm(false);
    await fetchData();
    toast.success(`${type} added`);
  };

  // Log status change
  const logStatusChange = async (creditorId, action) => {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("creditor_status_log").insert([{
      creditor_id: creditorId,
      action,
      logged_by: user?.user_metadata?.display_name || "Manual Entry"
    }]);
  };

  // Deactivate
  const deactivateCreditor = async (creditor) => {
    const balance = calculateTotals(creditor.id).balance;
    if (balance > 0) {
      toast.error("Cannot deactivate until balance is fully paid");
      return;
    }
    await supabase.from("debts").update({ status: "Inactive" }).eq("id", creditor.id);
    await logStatusChange(creditor.id, "Deactivated");
    await fetchData();
    toast.success("Creditor deactivated");
  };

  // Activate
  const activateCreditor = async (creditor) => {
    const balance = calculateTotals(creditor.id).balance;
    if (balance > 0) {
      toast.error("Cannot activate with outstanding balance");
      return;
    }
    await supabase.from("debts").update({ status: "Active" }).eq("id", creditor.id);
    await logStatusChange(creditor.id, "Activated");
    await fetchData();
    toast.success("Creditor activated");
  };

  // Delete
  const deleteCreditor = async (id) => {
    const confirmText = prompt("Type DELETE to confirm permanent removal:");
    if (confirmText !== "DELETE") { toast.info("Cancelled"); return; }
    await supabase.from("debts").delete().eq("id", id);
    await fetchData();
    toast.success("Creditor deleted");
  };

  // CSV Exports
  const exportSummaryCSV = () => {
    const headers = ["Creditor", "Total Loaned", "Total Paid", "Balance", "Status"];
    const rows = filteredCreditors.map(c => {
      const totals = calculateTotals(c.id);
      return [c.creditor, totals.totalLoaned, totals.totalPaid, totals.balance, c.status];
    });
    const blob = new Blob([headers.join(",") + "\n" + rows.map(r => r.join(",")).join("\n")], { type: "text/csv" });
    saveAs(blob, `debts_summary_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  const exportLedgerCSV = () => {
    const headers = ["Date", "Type", "Amount", "Notes", "Running Balance", "Logged By", "Logged At"];
    let running = 0;
    const rows = ledgerData.map(r => {
      running += r.type === "Loan" ? r.amount : -r.amount;
      return [
        r.date,
        r.type,
        r.amount,
        r.notes || "",
        running,
        r.logged_by || "",
        r.logged_at ? format(new Date(r.logged_at), "dd/MM/yyyy HH:mm") : ""
      ];
    });
    const blob = new Blob([headers.join(",") + "\n" + rows.map(r => r.join(",")).join("\n")], { type: "text/csv" });
    saveAs(blob, `ledger_${ledgerCreditor.creditor}_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  // Filtering
  const filteredCreditors = creditors.filter(c => {
    if (statusFilter !== "All" && c.status !== statusFilter) return false;
    if (creditorFilter && c.creditor !== creditorFilter) return false;
    return true;
  });

  const grandTotals = filteredCreditors.reduce((acc, c) => {
    const totals = calculateTotals(c.id);
    acc.loaned += totals.totalLoaned;
    acc.paid += totals.totalPaid;
    acc.balance += totals.balance;
    return acc;
  }, { loaned: 0, paid: 0, balance: 0 });

  return (
    <div className="main-content debts-container">
      <div className="main-heading">💳 Debts Management</div>

      {/* Add Creditor */}
      <div className="form-section">
        <div className="section-title">➕ Add New Creditor</div>
        <div className="form-grid">
          <label>Creditor Name:
            <input value={newCreditor} onChange={e => setNewCreditor(e.target.value)} />
          </label>
          <label>Description:
            <input value={newCreditorDesc} onChange={e => setNewCreditorDesc(e.target.value)} />
          </label>
          <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
            <button className="btn-entry" style={{ marginTop: 22 }} onClick={addCreditor}>Add Creditor</button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="form-section">
        <div className="section-title">🔍 Filters</div>
        <div className="form-grid">
          <label>Status:
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </label>
          <label>Creditor:
            <select value={creditorFilter} onChange={e => setCreditorFilter(e.target.value)}>
              <option value="">All</option>
              {creditors.map(c => <option key={c.id}>{c.creditor}</option>)}
            </select>
          </label>
          <label>From:
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </label>
          <label>To:
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          </label>
          <button className="btn-apply" style={{ marginTop: 22 }} onClick={fetchData}>Show Results</button>
          <button className="btn-export" style={{ marginTop: 22 }} onClick={exportSummaryCSV}>Export CSV</button>
        </div>
      </div>

      {/* Main Table */}
      <div style={{ overflowX: "auto" }}>
        <table className="styled-table">
          <thead>
            <tr>
              <th>Creditor</th>
              <th>Total Loaned</th>
              <th>Total Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCreditors.map(c => {
              const t = calculateTotals(c.id);
              return (
                <tr key={c.id} style={{ background: c.status === "Inactive" ? "#f3f3f3" : "white" }}>
                  <td>{c.creditor}</td>
                  <td>£{t.totalLoaned.toFixed(2)}</td>
                  <td>£{t.totalPaid.toFixed(2)}</td>
                  <td>£{t.balance.toFixed(2)}</td>
                  <td>{c.status}</td>
                  <td>
                    <button className="btn-edit" onClick={() => openLedger(c)}>View Ledger</button>
                    <button className="btn-apply" onClick={() => { setLedgerCreditor(c); setShowLoanForm(true); }}>Add Loan</button>
                    <button className="btn-apply" onClick={() => { setLedgerCreditor(c); setShowRepayForm(true); }}>Repay Debt</button>
                    {c.status === "Active" && t.balance === 0 && (
                      <button className="btn-apply" onClick={() => deactivateCreditor(c)}>Deactivate</button>
                    )}
                    {c.status === "Inactive" && t.balance === 0 && (
                      <button className="btn-apply" onClick={() => activateCreditor(c)}>Activate</button>
                    )}
                    <button className="btn-delete" onClick={() => deleteCreditor(c.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
            <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
              <td>Grand Total</td>
              <td>£{grandTotals.loaned.toFixed(2)}</td>
              <td>£{grandTotals.paid.toFixed(2)}</td>
              <td>£{grandTotals.balance.toFixed(2)}</td>
              <td colSpan="2"></td>
            </tr>
          </tbody>
        </table>
      </div>

     {/* Ledger Modal */}
      {showLedger && (
        <PortalModal>
          <div className="modal-dark">
            <div className="modal-content-dark">
              {/* ...modal content... */}
              <h3>📜 Ledger - {ledgerCreditor.creditor}</h3>
              <button className="btn-export" onClick={exportLedgerCSV}>Export Ledger CSV</button>
              <div style={{ overflowX: "auto" }}>
                <table>
                  {/* ...table content... */}
                  <thead>
                    <tr>
                      <th>Date</th><th>Type</th><th>Amount</th><th>Notes</th><th>Running Balance</th><th>Logged By</th><th>Logged At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let running = 0;
                      return ledgerData.map((r, i) => {
                        running += r.type === "Loan" ? r.amount : -r.amount;
                        return (
                          <tr key={i} style={{ background: r.notes?.toLowerCase().startsWith("correction:") ? "#222d" : "transparent" }}>
                            <td>{r.date}</td>
                            <td>{r.type}</td>
                            <td>£{r.amount.toFixed(2)}</td>
                            <td>{r.notes}</td>
                            <td style={{ color: r.type === "Loan" ? "crimson" : "seagreen" }}>£{running.toFixed(2)}</td>
                            <td>{r.logged_by}</td>
                            <td>{r.logged_at ? format(new Date(r.logged_at), "dd/MM/yyyy HH:mm") : ""}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <button className="btn-apply" onClick={() => setShowLedger(false)}>Close</button>
            </div>
          </div>
        </PortalModal>
      )}

 {/* Loan Form */}
{showLoanForm && (
  <PortalModal>
    <div className="modal-dark">
      <div className="modal-content-dark">
        <h3>➕ Add Loan - {ledgerCreditor.creditor}</h3>
        <label>Amount (£):</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formAmount}
          onChange={e => setFormAmount(e.target.value)}
        />
        <label>Notes:</label>
        <input
          type="text"
          value={formNotes}
          onChange={e => setFormNotes(e.target.value)}
        />
        <div className="correction-row">
          <input
            type="checkbox"
            checked={isCorrection}
            onChange={e => setIsCorrection(e.target.checked)}
            id="correction"
          />
          <label htmlFor="correction" style={{ margin: 0 }}>This is a correction</label>
        </div>
        <button className="btn-entry" onClick={() => addTransaction("Loan")}>Save Loan</button>
        <button className="btn-apply" onClick={() => setShowLoanForm(false)}>Cancel</button>
      </div>
    </div>
  </PortalModal>
)}

  {/* Repay Form */}
{showRepayForm && (
  <PortalModal>
    <div className="modal-dark">
      <div className="modal-content-dark">
        <h3>💵 Repay Debt - {ledgerCreditor.creditor}</h3>
        <label>Amount (£):</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formAmount}
          onChange={e => setFormAmount(e.target.value)}
        />
        <label>Notes:</label>
        <input
          type="text"
          value={formNotes}
          onChange={e => setFormNotes(e.target.value)}
        />
        <div className="correction-row">
          <input
            type="checkbox"
            checked={isCorrection}
            onChange={e => setIsCorrection(e.target.checked)}
            id="correction-repay"
          />
          <label htmlFor="correction-repay" style={{ margin: 0 }}>This is a correction</label>
        </div>
        <button className="btn-entry" onClick={() => addTransaction("Payment")}>Save Payment</button>
        <button className="btn-apply" onClick={() => setShowRepayForm(false)}>Cancel</button>
      </div>
    </div>
  </PortalModal>
)}
 
    </div>
  );
}